import { Button } from '@accure/ui';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { Download } from 'lucide-react';

import {
  type JobDetails,
  type JobResult,
} from '@/api/fe-api/domain/entities/capacity-evaluation';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { PowerUnit } from '@/entities/power-unit';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import { energyLabels, formatEnergy } from '@/utils/format/battery';
import { plural, singular } from '@/utils/format/label';
import { formatPercentage } from '@/utils/format/number';

export type SectionThroughputTableProps = {
  labels: [string, string];
  capacityTest: JobDetails;
  results: JobResult[];
};

type TableItem = {
  id: string;
  frontendId: string;
  chargeSoh: number;
  chargeEnergy: number;
  dischargeSoh: number;
  dischargeEnergy: number;
};

export function SectionThroughputTable({
  labels,
  capacityTest,
  results,
}: SectionThroughputTableProps) {
  const { config } = useMe();
  const { asset } = useCurrentAsset();

  const fileNameComponents = {
    date: format(capacityTest.createdAt, 'yyMMdd'),
    title: 'Capacity-Test-Result',
    asset: asset?.name?.replace(/\s+/g, '-'),
    level: singular(labels),
  };

  const tableItems: TableItem[] = results.map((item) => ({
    id: item.id,
    frontendId: item.fullFrontendId,
    chargeSoh: item.chargingSohC,
    chargeEnergy: item.chargingEnergy,
    dischargeSoh: item.dischargingSohC,
    dischargeEnergy: item.dischargingEnergy,
  }));

  // (Date_Title_Asset_Level.csv)
  const fileName = Object.values(fileNameComponents).join('_') + '.csv';

  function downloadTable() {
    const headers = [
      'ID',
      'SOH Charge (decimal)',
      `Charged Energy (${energyLabels[PowerUnit.W]})`,
      'SOH Discharge (decimal)',
      `Discharged Energy (${energyLabels[PowerUnit.W]})`,
    ];

    const rows = tableItems.map((row) => [
      row.frontendId,
      row.chargeSoh,
      row.chargeEnergy,
      row.dischargeSoh,
      row.dischargeEnergy,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.join(',')),
    ].join('\n');

    saveAs(
      new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }),
      fileName,
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h4 className="text-lg font-semibold">{plural(labels)}</h4>
        <Tooltip content="Download table as a CSV" delay>
          <Button variant="outline" onClick={downloadTable}>
            <Download />
            Download Table
          </Button>
        </Tooltip>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>&nbsp;</TableHeader>
            <TableHeader className="w-full">ID</TableHeader>
            <TableHeader className="min-w-48 text-right">
              <div>Charged</div>
              <div className="text-xs font-normal">(SOH-C/Energy)</div>
            </TableHeader>
            <TableHeader className="min-w-48 text-right">
              <div>Discharged</div>
              <div className="text-xs font-normal">(SOH-C/Energy)</div>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableItems.map((item: TableItem, index: number) => (
            <TableRow key={`${item.id}-${item.chargeSoh}`}>
              <TableCell className="text-right">{index + 1}</TableCell>
              <TableCell>{item.frontendId}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-baseline justify-end">
                  <span>{formatPercentage(item.chargeSoh)}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    ({formatEnergy(item.chargeEnergy, config.powerUnit, 2)})
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-baseline justify-end">
                  <span>{formatPercentage(item.dischargeSoh)}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    ({formatEnergy(item.dischargeEnergy, config.powerUnit, 2)})
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
